import { useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { useTruncationTooltip } from 'components/Kizen/Tooltip';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';

import { TextSpan } from 'app/typography';
import {
  StyledLabelWrapper,
  StyledLabel,
  StyledFileInputWrapper,
  StyledPlaceholder,
  StyledFile,
  UploadContainer,
  PreviewImageContainer,
} from './styles';
import { IconWithTooltip } from 'pages/Common/components/LinkWithIconTooltip/IconWithTooltip';
import RemoveFileIcon from './remove-file.svg?react';

export const UPLOAD_INPUT_TYPES = {
  AVATAR: 'AVATAR',
  LOGO: 'LOGO',
};

export const ACCEPTED_FILES = 'image/jpeg,image/png,image/gif,image/svg+xml';

const DashedBorder = ({ children }) => (
  <>
    {children}
    <svg
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute top-0 left-0 pointer-events-none"
    >
      <rect
        width="100%"
        height="100%"
        fill="none"
        rx="4"
        ry="4"
        stroke="currentColor"
        strokeWidth="2"
        strokeDasharray="4,8"
        strokeLinecap="square"
      />
    </svg>
  </>
);

const FileInputWithImage = ({
  className = '',
  label = '',
  labelInfo = null,
  actionLabel,
  onChange,
  onRemove = () => {},
  id = '',
  imagePreviewSource = { name: '', url: '' },
  type = UPLOAD_INPUT_TYPES.LOGO,
  file = null,
  showImage = true,
  acceptedExtensions = ACCEPTED_FILES,
  ...props
}) => {
  const [filenameProps, filenameTooltip] = useTruncationTooltip();

  const inputFile = useRef(null);

  const { t } = useTranslation();

  const onDrop = useCallback(
    (acceptedFile) => onChange(acceptedFile),
    [onChange]
  );

  const { getRootProps } = useDropzone({ onDrop });

  const handleFileUpload = () => inputFile.current && inputFile.current.click();

  const removeFile = ({ event }) => {
    event.stopPropagation();
    inputFile.current.value = null;
    onRemove();
  };

  return (
    <div className={className}>
      <StyledLabelWrapper htmlFor={id} {...getRootProps()}>
        {label ? (
          <StyledLabel>
            {label}
            {labelInfo ? <IconWithTooltip label={labelInfo} /> : null}
          </StyledLabel>
        ) : null}
        <UploadContainer>
          <input
            ref={inputFile}
            type="file"
            hidden
            onChange={(e) => onChange(e.target.files)}
            accept={acceptedExtensions}
            {...props}
          />

          {showImage ? (
            <PreviewImageContainer
              type={type}
              url={imagePreviewSource.url}
              onClick={handleFileUpload}
              style={{
                backgroundImage:
                  type === UPLOAD_INPUT_TYPES.AVATAR
                    ? `url(${imagePreviewSource.url})`
                    : '',
              }}
            >
              {!imagePreviewSource && type === UPLOAD_INPUT_TYPES.AVATAR && (
                <TextSpan>Preview</TextSpan>
              )}
              {imagePreviewSource &&
                type === UPLOAD_INPUT_TYPES.LOGO &&
                imagePreviewSource.url && (
                  <img src={imagePreviewSource.url} alt="Logo" />
                )}
            </PreviewImageContainer>
          ) : null}

          {type === UPLOAD_INPUT_TYPES.LOGO && (
            <Button variant="text" onClick={handleFileUpload}>
              {actionLabel}
            </Button>
          )}

          {type === UPLOAD_INPUT_TYPES.AVATAR && (
            <StyledFileInputWrapper
              onClick={handleFileUpload}
              showImage={showImage}
            >
              <DashedBorder>
                {(file || (imagePreviewSource && imagePreviewSource.url)) && (
                  <StyledFile
                    created={
                      (file && file.created !== false) || imagePreviewSource
                    }
                  >
                    <button
                      type="button"
                      onClick={(event) => removeFile({ event })}
                    >
                      <RemoveFileIcon />
                      {t('remove file')}
                    </button>
                    {filenameTooltip}
                    <TextEllipsisWithTooltip {...filenameProps}>
                      {(file && (file.name || file.path)) ||
                        imagePreviewSource.name}
                    </TextEllipsisWithTooltip>
                  </StyledFile>
                )}
                <StyledPlaceholder>
                  {file ? t('Change File') : t('Add File')}{' '}
                  <span>{t('or drop file here')}</span>
                </StyledPlaceholder>
              </DashedBorder>
            </StyledFileInputWrapper>
          )}
        </UploadContainer>
      </StyledLabelWrapper>
    </div>
  );
};

export default FileInputWithImage;
